/**
 * The overlay's one convention for turning a magnitude into text, and the one
 * DOM piece for pairing that text with the resource it belongs to (an icon).
 * Every resource count, cost, and rate the overlay shows routes through here,
 * so the K/M/… notation and the digit count (both really owned by `@core`'s
 * `format`) can't quietly drift between call sites — see docs/ui.md.
 */
import { format, gt, lt, toNumber, type Notation, type NumSource } from '@core/index.js';
import { el } from './dom.js';

/** Every amount reads as ~4 significant digits — `1.000`, `10.00`, `100.0` —
 * so a fixed-width slot (a buy button, a column of per-tick contributions)
 * never wraps or jiggles: more integer digits just means fewer decimals.
 * That shrink-as-digits-grow rule only holds up once there's a suffix
 * (`10.12 K`, `100.2 K` — mantissa + " X" is a constant 7 characters); below
 * 1000 there's no suffix to make up the width, so those values keep the full
 * 3 decimals throughout instead of shrinking to 1 (`102.200`, not `102.2`). */
const SIGNIFICANT_DIGITS = 4;
const MAX_DECIMALS = SIGNIFICANT_DIGITS - 1;

function decimalsFor(integerDigits: number): number {
  return Math.max(SIGNIFICANT_DIGITS - integerDigits, 0);
}

/** `1.000 K` / `10.00 K` / `100.0 K` (or `1.000e4` under scientific notation)
 * — see the module doc. Two passes: the first (at the maximum decimals)
 * reveals how many integer digits the value/mantissa has, the second formats
 * with exactly the decimals that leaves at 4 significant digits (suffixed
 * values only — unsuffixed ones always keep the max decimals). */
export function formatAmount(value: NumSource, notation: Notation): string {
  if (lt(value, 1000) && gt(value, -1000)) {
    return format(value, MAX_DECIMALS, notation, true);
  }
  const probe = format(value, MAX_DECIMALS, notation, true);
  const integerDigits = /^-?(\d+)\./.exec(probe)?.[1]?.length ?? 1;
  return format(value, decimalsFor(integerDigits), notation, true);
}

/** `+1.000 K` for a gain, `-1.000 K` for a drain — sign always explicit, so a
 * rate reads unambiguous next to others. */
export function formatSignedAmount(value: NumSource, notation: Notation): string {
  const text = formatAmount(value, notation);
  return text.startsWith('-') ? text : `+${text}`;
}

/** A comma-grouped exact integer, for a detail popup that wants the real
 * count behind an abbreviated `1.000 M` — falls back to scientific past double
 * precision (break_infinity territory, where "exact" stops being meaningful). */
export function formatExact(value: NumSource): string {
  const n = toNumber(value);
  return Number.isFinite(n)
    ? Math.round(n).toLocaleString('en-US')
    : format(value, SIGNIFICANT_DIGITS - 1, 'scientific');
}

/**
 * A content-art icon: hidden until it loads, hidden again on error — dev
 * builds usually lack the `/content/` renders (they're fetched from R2 at
 * build time), and a missing image must not leave a broken-image glyph in
 * the row.
 */
export function artIcon(className: string): {
  readonly img: HTMLImageElement;
  set(url: string | undefined): void;
} {
  const img = el('img', { class: className });
  img.alt = '';
  img.hidden = true;
  img.addEventListener('load', () => {
    img.hidden = false;
  });
  img.addEventListener('error', () => {
    img.hidden = true;
  });
  return {
    img,
    set(url: string | undefined): void {
      if (url === undefined) {
        img.hidden = true;
        return;
      }
      if (img.dataset.src !== url) {
        img.dataset.src = url;
        img.src = url;
      }
    },
  };
}

/** A formatted amount tied to the resource it's in or of — an icon plus text.
 * The recurring shape for a cost or a per-tick contribution: anywhere a
 * number belongs to a specific resource, not just a bare readout. */
export interface AmountView {
  readonly root: HTMLElement;
  /** `display` is already formatted (`formatAmount`/`formatSignedAmount`/
   * `formatExact`) — this only wires it into the DOM and swaps the icon. */
  set(display: string, resourceArtUrl?: string): void;
}

export function amountView(
  rootClass: string,
  iconClass: string,
  amountClass: string,
): AmountView {
  const icon = artIcon(iconClass);
  const amount = el('span', { class: amountClass });
  const root = el('span', { class: rootClass }, [icon.img, amount]);
  return {
    root,
    set(display, resourceArtUrl): void {
      icon.set(resourceArtUrl);
      amount.textContent = display;
    },
  };
}
